import { Button, Dialog, DialogActions, Box, DialogContent, IconButton, DialogTitle, Typography, FormHelperText, TextField, RadioGroup, Radio, Alert, Divider } from '@mui/material';
import { CustomSingleSelect } from '../../utils/CustomSingleSelect';
import { CustomErrorLogger } from '../../utils/CustomErrorLogger';
import { useState, useCallback, useRef } from 'react';
import uploadIcon from '../../images/upload.svg';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { useFormik } from 'formik';
import Iconify from '../Iconify';
import * as Yup from 'yup';
import axios from 'axios';
import '../../css/invoices/addInvoiceDialog.css';

export const AddInvoiceDialoge = ({ openAddInvoice, setOpenAddInvoice, machine_ids }) => {

    const { t } = useTranslation();
    const navigate = useNavigate();

    const [forContract, setForContract] = useState(false);
    const [documents, setDocuments] = useState([]);
    const [errMsg, setErrMsg] = useState(null);

    const fileInputRef = useRef(null);

    const formik = useFormik({

        initialValues: {
            title: '',
            invoice_number: '',
            company: '',
            contract: '',
            date: '',
            documents: [],
            machines: machine_ids || [],
        },

        validationSchema: Yup.object().shape({
            title: Yup.string().required(t('isRequired')),
            invoice_number: Yup.string().required(t('isRequired')),
            company: forContract ? Yup.object().optional() : Yup.object().required(t('isRequired')),
            contract: forContract ? Yup.object().required(t('isRequired')) : Yup.object().optional(),
            date: Yup.date().required(t('isRequired')),
            documents: Yup.array().optional(),
        }),

        onSubmit: async (values) => {
            try {
                const formData = new FormData();

                // Append files
                documents.forEach((file) => {
                    formData.append('files', file.file);
                });

                // Append simple fields
                formData.append('title', values.title.trim());
                formData.append('invoice_number', values.invoice_number.trim());
                formData.append('company_id', values.company?.value || '');
                formData.append('date', new Date(values.date).toISOString());
                if (values.contract?.value) {
                    formData.append('contract_id', values.contract.value);
                }
                if (values.machines.length > 0) {
                    formData.append(
                        'machines',
                        JSON.stringify(values.machines.map(machine => machine.id || machine.value || machine))
                    );
                }

                // Send the FormData object via Axios
                const response = await axios.post('invoice', formData, {
                    headers: {
                        'Content-Type': 'multipart/form-data',
                    },
                });

                if (response.status === 200 || response.status === 201) {
                    navigate(`/dashboard/factures/facture_details/${response.data.id}`)
                }
            } catch (error) {
                console.error('Error adding invoice:', error);
                CustomErrorLogger(error, 'Error adding invoice in AddInvoiceDialog');
                setErrMsg(error?.response?.data?.message || error.message || 'erreur lors de la création de la facture');
            }
        },
    });

    const { handleSubmit, values, handleChange, handleBlur, touched, errors, setFieldValue } = formik;

    const handleClose = useCallback(() => {
        setOpenAddInvoice(false);
    }, [setOpenAddInvoice]);

    const fetchContracts = useCallback(async (page, limit, search) => {
        try {
            const response = await axios.get('contract/findAllForInvoice', {
                params: {
                    page,
                    limit,
                    search,
                },
            });

            if (response.status === 200 || response.status === 201) {
                return response.data.map(contract => ({
                    value: contract.id,
                    label: contract.contract_number
                        ? `𝗧𝗶𝘁𝗿𝗲: ${contract.title}, 𝗡𝘂𝗺é𝗿𝗼 𝗱𝘂 𝗰𝗼𝗻𝘁𝗿𝗮𝘁: ${contract.contract_number}, 𝗘𝗻𝘁𝗿𝗲𝗽𝗿𝗶𝘀𝗲: ${contract.company.name}`
                        : `𝗧𝗶𝘁𝗿𝗲: ${contract.title}, 𝗘𝗻𝘁𝗿𝗲𝗽𝗿𝗶𝘀𝗲: ${contract.company.name}`,
                    company: contract.company,
                    contract_site: contract.contract_site,
                    machine: contract.contract_date[0].contract_machine,
                }));
            }

            return [];
        } catch (error) {
            console.error('Error fetching contracts:', error);
            CustomErrorLogger(error, "error fetching contracts in AddInvoiceDialoge");
        }
    }, []);

    const handleCreateCompany = useCallback(async (newOption) => {
        try {
            const response = await axios.post('company', { name: newOption.label });
            if (response.status === 200 || response.status === 201) {
                setFieldValue('company', { value: response.data.id, label: response.data.name });
            }
        } catch (error) {
            console.error('Error creating company in AddInvoiceDialoge:', error);
            CustomErrorLogger(error, "Error creating company in AddInvoiceDialoge");
        }
    }, [setFieldValue]);

    const fetchCompanies = useCallback(async (offset, limit, search) => {
        try {
            const response = await axios.get('company', {
                params: {
                    offset,
                    limit,
                    filters: { debouncedSearch: search },
                },
            });
            if (response.status === 200 || response.status === 201) {
                return response.data.companiesData.map(company => ({
                    value: company.id,
                    label: company.name,
                }));
            }
        } catch (error) {
            console.error('Error fetching companies:', error);
            CustomErrorLogger(error, "error fetching companies in AddInvoiceDialoge");
        }
    }, []);

    const onChangeContract = (value) => {

        setFieldValue('contract', value);
        if (value) {
            setFieldValue('company', { value: value.company.id, label: value.company.name });
            setFieldValue('machines', value.machine.map((machine) => ({ value: machine.machine.id, label: machine.machine.name })));
            return;
        }
        setFieldValue('company', '');
        setFieldValue('machines', []);
    };

    const handleUploadClick = () => fileInputRef.current.click();

    const handleUploadDocuments = (files) => {

        const newFiles = Array.from(files).map(file => ({
            name: file?.name,
            size: (file?.size / (1024 * 1024))?.toFixed(2), // Convert size to MB
            file: file
        }));
        setDocuments(prevDocuments => [...prevDocuments, ...newFiles]);
    };

    const handleRemoveDocument = (index) => {
        setDocuments(prevDocuments => prevDocuments.filter((_, i) => i !== index));
    };

    return (
        <Dialog open={openAddInvoice} fullWidth maxWidth="md">
            <IconButton onClick={handleClose} className='addInvoice_icon_button'>
                <Iconify icon="material-symbols:close" className='addInvoice_close_icon' />
            </IconButton>
            <DialogTitle className='addInvoice_dialog_title'>{t('addInvoiceDialogeTitle')}</DialogTitle>
            <Divider />
            {errMsg && <Alert severity="error">{errMsg}</Alert>}
            <form onSubmit={handleSubmit}>
                <DialogContent className='addInvoice_dialog_content'>
                    <Box className="addInvoice_title_number_box">
                        <Box className="addInvoice_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Numéro de la facture</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le numero'
                                value={values.invoice_number}
                                className='addInvoice_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="invoice_number"
                                error={touched.invoice_number && Boolean(errors.invoice_number)}
                                helperText={touched.invoice_number && errors.invoice_number}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                        <Box className="addInvoice_helper_field_box">
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Titre de la facture</FormHelperText>
                            <TextField
                                fullWidth
                                placeholder='Entrez le titre'
                                value={values.title}
                                className='addInvoice_InputField'
                                id="outlined-adornment-weight"
                                onChange={handleChange}
                                onBlur={handleBlur}
                                name="title"
                                error={touched.title && Boolean(errors.title)}
                                helperText={touched.title && errors.title}
                                aria-describedby="outlined-weight-helper-text"
                            />
                        </Box>
                    </Box>
                    <Box className="addInvoice_title_number_box">
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Date de facture</FormHelperText>
                            <TextField
                                sx={{ width: '100%' }}
                                className={`addInvoice_InputField`}
                                id="outlined-adornment-weight"
                                type='date'
                                value={values.date}
                                onChange={(event) => setFieldValue('date', event.target.value)}
                                onBlur={handleBlur}
                                name="date"
                                aria-describedby="outlined-weight-helper-text"
                                error={touched.date && Boolean(errors.date)}
                                helperText={touched.date && errors.date}
                            />
                        </Box>
                    </Box>
                    <Box className="addInvoice_title_number_box">
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Type de facture</FormHelperText>
                            <RadioGroup
                                name="contractAssociation"
                                className='addInvoice_radio_group'
                                value={forContract}
                                onChange={(e) => setForContract(e.target.value === 'true')}
                            >
                                <Box className='addInvoice_radio_box'>
                                    <Radio value={false} />
                                    <Typography className="addInvoice_radio_label">Sans contrat</Typography>
                                </Box>
                                <Box className='addInvoice_radio_box'>
                                    <Radio value={true} />
                                    <Typography className="addInvoice_radio_label">Associée à un contrat</Typography>
                                </Box>
                            </RadioGroup>

                        </Box>
                    </Box>
                    {forContract &&
                        <Box className='addInvoice_helper_field_box'>
                            <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Sélection du contrat concerné</FormHelperText>
                            <CustomSingleSelect
                                placeholder="Sélectionnez un contrat"
                                className='addInvoice_InputField'
                                required={true}
                                value={values.contract}
                                fetchFunction={fetchContracts}
                                backendSearchable={true}
                                onChange={onChangeContract}
                                helperText={touched.contract && errors.contract}
                                error={touched.contract && Boolean(errors.contract)}
                            />
                        </Box>
                    }
                    <Box className='addInvoice_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Entreprise associée au facture</FormHelperText>
                        {(forContract && values.company?.label) &&
                            <Box className="ships_box">
                                <Typography className="ships_label">{values.company?.label}</Typography>
                            </Box>
                        }
                        {!forContract &&
                            <CustomSingleSelect
                                placeholder='Sélectionnez une entreprise'
                                className='addInvoice_InputField'
                                required={true}
                                value={values.company}
                                fetchFunction={fetchCompanies}
                                backendSearchable={true}
                                onChange={(value) => setFieldValue('company', value)}
                                creatable={true}
                                createFunction={handleCreateCompany}
                                helperText={touched.company && errors.company}
                                error={touched.company && Boolean(errors.company)}
                            />
                        }
                    </Box>
                    <Box className='addInvoice_helper_field_box'>
                        <FormHelperText id="outlined-weight-helper-text" className="addInvoiceInputTitle">Document de la facture (optionnel)</FormHelperText>
                        <Box className='modifierPhoto_upload_box' onClick={handleUploadClick}>
                            <img src={uploadIcon} alt="icon" className='modifierPhoto_upload_icons' loading='lazy' decoding='async' />
                            <Box>
                                <Typography className='modifierPhoto_uplaod_first_typography'>Glisser-déposez le fichier ici, ou Parcourir</Typography>
                                <Typography className='modifierPhoto_uplaod_second_typography'>Format PDF de moins de 25Mo</Typography>
                            </Box>
                        </Box>
                        <input
                            type="file"
                            accept=".pdf"
                            ref={fileInputRef}
                            multiple
                            style={{ display: "none" }}
                            onChange={event => handleUploadDocuments(event.target.files)}
                        />
                    </Box>
                    <Box className='addInvoice_documents_box'>
                        {documents.map((doc, index) => (
                            <Box
                                className='addInvoice_document_item'
                                key={index}
                            >
                                <Iconify className='addInvoice_pdf_icon' icon="bxs:file-pdf" />
                                <Typography className='addInvoice_doc_name'>{doc.name}</Typography>
                                <Typography className='addInvoice_doc_size'>{doc.size} MB</Typography>
                                <Iconify className='addInvoice_delete_icon' icon="ic:outline-delete" onClick={() => handleRemoveDocument(index)} />
                            </Box>
                        ))}
                    </Box>
                </DialogContent>
                <DialogActions className='addInvoice_dialog_actions'>
                    <Button className='addInvoice_button_annuler' onClick={handleClose}>{t('Annuler')}</Button>
                    <Button className='addInvoice_button_valider' variant="contained" type="submit">{t('Valider')}</Button>
                </DialogActions>
            </form>
        </Dialog>
    );
};